/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Box, Button, CircularProgress, Typography } from '@material-ui/core';
import { PayloadAction } from '@reduxjs/toolkit';
import Header from 'components/Header/Header';
import { createForm, FormApi } from 'final-form';
import { Component } from 'react';
import { Field, Form as FinalForm } from 'react-final-form';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { subscribeActionAfter } from 'redux-subscribe-action';
import { AppDispatch, AppState } from 'store';
import { chooseKenticoProject } from 'store/auth/auth.actions';
import { SettingsActionTypes } from 'store/settings/settings.actions.types';
import { getAuthSpinnerSelector } from 'store/spinner/spinner.selectors';
import { IAutocompleteField } from 'types/shared';
import { requiredAutocomplete } from 'utils/customValidators';
import { KontentProjectOverviewDTO } from 'utils/restApplicationClient';
import { getKenticoProjects } from '../../../store/settings/settings.actions';
import CustomSelect from '../Input/CustomSelect/CustomSelect';
import {
  ChooseProjectContainer,
  ChooseProjectFormContainer,
  ChooseProjectPageContainer,
  ChooseProjectTopBar,
} from './ChooseProject.styled';

interface IFormValues {
  chosenProject: IAutocompleteField<KontentProjectOverviewDTO>;
}

interface IState {
  kenticoProjects: KontentProjectOverviewDTO[];
}

interface IStateProps {
  spinner: boolean;
}

interface IDispatchProps {
  getKenticoProjects: () => AppDispatch;
  chooseKenticoProject: (payload: KontentProjectOverviewDTO) => AppDispatch;
}

type PropType = WithTranslation & IDispatchProps & IStateProps;

class ChooseProject extends Component<PropType, IState> {
  private _unsubscribe: (() => void)[] = [];

  constructor(props: PropType) {
    super(props);
    this.state = {
      kenticoProjects: [],
    };
    this.form = createForm({
      onSubmit: this.handleSubmit,
      destroyOnUnregister: true,
    });
  }

  componentDidMount(): void {
    const { getKenticoProjects } = this.props;
    const kenticoProjectsSubscription = subscribeActionAfter(SettingsActionTypes.GetKenticoProjectsSuccess, (action) =>
      this.setState({ kenticoProjects: (action as PayloadAction<KontentProjectOverviewDTO[]>).payload }),
    );

    getKenticoProjects();
    this._unsubscribe.push(kenticoProjectsSubscription);
  }

  componentWillUnmount(): void {
    this._unsubscribe.forEach((unsubscribe) => unsubscribe());
  }

  form: FormApi<IFormValues>;

  handleSubmit = ({ chosenProject: { value } }: IFormValues): void => {
    const { chooseKenticoProject } = this.props;

    chooseKenticoProject(value);
  };

  render(): JSX.Element {
    const { t, spinner } = this.props;
    const { kenticoProjects } = this.state;

    return (
      <ChooseProjectPageContainer>
        <Header />
        <Box display="flex" alignItems="center" marginTop="144px" flexDirection="column">
          <ChooseProjectContainer>
            <ChooseProjectTopBar>
              <Typography color="textSecondary" component="div">
                <Box fontWeight="fontWeightBold" color="#748392">
                  {t('loginForm.logged')}
                </Box>
              </Typography>
            </ChooseProjectTopBar>
            <ChooseProjectFormContainer>
              <FinalForm
                onSubmit={this.handleSubmit}
                form={this.form}
                subscription={{ submitting: true, pristine: true }}
                render={({ handleSubmit }): JSX.Element => (
                  <form onSubmit={handleSubmit}>
                    <Field
                      name="chosenProject"
                      component={CustomSelect}
                      validate={requiredAutocomplete}
                      label="loginForm.chosenProject"
                      placeholder="common.selectPlaceholder"
                      options={kenticoProjects.map((project) => ({
                        label: project.kenticoProjectName,
                        value: project,
                      }))}
                      spinner={spinner}
                    />
                    <Box marginTop="18px">
                      <Button fullWidth variant="contained" color="secondary" type="submit" disabled={spinner}>
                        {spinner ? <CircularProgress size={24} color="secondary" /> : t('common.save')}
                      </Button>
                    </Box>
                  </form>
                )}
              />
            </ChooseProjectFormContainer>
          </ChooseProjectContainer>
        </Box>
      </ChooseProjectPageContainer>
    );
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AppDispatch>): IDispatchProps => ({
  getKenticoProjects: (): AppDispatch => dispatch(getKenticoProjects()),
  chooseKenticoProject: (payload: KontentProjectOverviewDTO): AppDispatch => dispatch(chooseKenticoProject(payload)),
});

const mapStateToProps = (state: AppState): IStateProps => ({
  spinner: getAuthSpinnerSelector(state),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(ChooseProject));
